/// <amd-module name="DistributedTaskUI/Logs/TaskLogStepComponent" />
import * as React from "react";

import { LogsSummaryView } from "DistributedTaskUI/Logs/LogsSummaryView";
import { LiveLogsParser } from "DistributedTaskUI/Logs/LiveLogsParser";
import { ITaskLog, TaskState, IssueType, ILogLine } from "DistributedTaskUI/Logs/Logs.Types";
import { LogsTelemetryHelper } from "DistributedTaskUI/Logs/LogsTelemetryHelper";
import { LogsViewUtility } from "DistributedTaskUI/Logs/LogsViewUtility";
import { StepComponent } from "DistributedTaskUI/Logs/StepComponent";
import * as Resources from "DistributedTaskUI/Logs/Resources";

import { Button } from "VSSUI/Button";
import { css } from "VSSUI/Util";
import { Statuses } from "VSSUI/Status";

import { TimerManagement } from "VSS/Core/TimerManagement";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Duration } from "VSSUI/Duration";
import { VssIconType } from "VSSUI/VssIcon";

import "VSS/LoaderPlugins/Css!DistributedTaskUI/Logs/TaskLogStepComponent";

export interface ITaskLogStepComponentProps {
    cssClass?: string;
    task: ITaskLog;
    showExpandedLogsView: (task: ITaskLog, lineNumber?: number) => void;
    isContextMenuVisible?: boolean;
    pageContext?: IVssPageContext;
    taskHelpUrl?: string;
}

interface ITaskLogStepComponentState {
    shouldShowDownloadLogButton: boolean;
    shouldShowTroubleShootErrorButton: boolean;
    initialTaskData: ILogLine[];
}

const LogsViewUtility2 = LogsViewUtility; // This is for decreasing size of the minified script
const c_MaxErrorsLinesToShow = 20;
const c_MaxCharactersToShowLiveLog = 5000;

export class TaskLogStepComponent extends React.Component<ITaskLogStepComponentProps, ITaskLogStepComponentState> {

    constructor(props: ITaskLogStepComponentProps) {
        super(props);

        this.state = {
            shouldShowDownloadLogButton: false,
            shouldShowTroubleShootErrorButton: false,
            initialTaskData: []
        };

        this._fetchLogsFromLogUrl = new TimerManagement().throttle(this._fetchLogsFromLogUrl, 2000);
    }

    public render(): React.ReactNode {
        const task = this.props.task;
        if (task) {
            const ariaLabel = this._getTaskOverviewAriaLabel();
            const ariaDescription = this._getTaskOverviewAriaDescription();
            const clickableTitle = LogsViewUtility2.showExpandViewEnabled(task);
            const hasSeparator = task.state !== TaskState.InProgress || !!task.percentComplete;
            const errors = LogsViewUtility2.getIssuesWithType(IssueType.Error, task);
            const warnings = LogsViewUtility2.getIssuesWithType(IssueType.Warning, task);
            const taskStatus = LogsViewUtility2.getTaskStatus(task.state, errors ? errors.length : 0, task.percentComplete);
            const warningStatus = LogsViewUtility2.getWarningStatus(warnings ? warnings.length : 0);
            const iconClassName = css("left-section-task-status-icon", "log-task-state", taskStatus.className);
            const uniqueKey = task.timelineRecordId || task.taskId;
            const describedById: string = "task-log-step-view-description" + uniqueKey;
            const tooltipContent: string = clickableTitle ? Resources.TaskLogExpandViewButtonName : "";
            let stepStatus = [{ status: taskStatus.status, className: taskStatus.className }];
            if (warningStatus) {
                stepStatus.push({ status: warningStatus.status, className: warningStatus.className });
            }

            return (
                <div
                    data-is-focusable={true}
                    role="listitem"
                    className={css(this.props.cssClass, "task-log-view-container")}
                    onMouseOver={this._onMouseOver}
                    onBlur={this._onBlur}
                    onFocus={this._onFocus}
                    onMouseLeave={this._onMouseLeave}
                    aria-label={ariaLabel}
                    aria-describedby={describedById}>
                    <div id={describedById} className="hidden">{ariaDescription}</div>
                    <StepComponent
                        uniqueKey={uniqueKey}
                        buttonAriaDescription={Resources.ExpandViewAriaDescription}
                        isTitleClickable={clickableTitle}
                        title={task.name}
                        infoProps={{ tooltipContent: task.resultCode, iconName: "Info", iconAriaLabel: Resources.Info }}
                        hasSepartor={hasSeparator}
                        stepStatuses={stepStatus}
                        // Remove iconProps in M143
                        iconProps={{ className: iconClassName, iconName: taskStatus.iconName, iconType: VssIconType.fabric, title: taskStatus.status }}
                        {...taskStatus.statusProps ? {
                            statusProps: {
                                statusProps: taskStatus.statusProps
                            }
                        } : {}}
                        onActionClick={this._onShowLogClick}
                        cssClass={"task-log-step-content"}
                        tooltipContent={tooltipContent}
                        rightSection={this._getRightSection()}
                    >
                    </StepComponent>
                    {
                        LogsViewUtility2.isLiveComponentVisible(task) && this._getLiveLogComponent(task)
                    }
                    {
                        !LogsViewUtility2.isLiveComponentVisible(task) && this._getLogsErrorComponent()
                    }
                </div >
            );
        } else {
            return null;
        }
    }

    public componentDidMount() {
        this._fetchLogsFromLogUrl();
    }

    private _getTaskOverviewAriaLabel(): string {
        const task = this.props.task;
        const warnings = LogsViewUtility2.getIssuesWithType(IssueType.Warning, task);
        const warningStatus = LogsViewUtility2.getWarningStatus(warnings ? warnings.length : 0);
        if (task) {
            const errors = LogsViewUtility2.getIssuesWithType(IssueType.Error, task);
            const taskStatus = LogsViewUtility2.getTaskStatus(task.state, errors ? errors.length : 0);
            let percentComplete = "";
            if (LogsViewUtility2.isTaskInProgress(task)) {
                if (task.percentComplete) {
                    percentComplete = format(Resources.PercentComplete, task.percentComplete);
                }
                return format(Resources.InProgressTaskLogStepOverviewAriaLabel, task.name, Resources.InProgress,
                    percentComplete, LogsViewUtility2.getTimeDiffFriendlyString(task.startTime, task.finishTime));
            } else if (LogsViewUtility2.hasTaskCompleted(task)) {
                return format(Resources.CompletedTaskLogStepOverviewAriaLabel, task.name, taskStatus.status, warningStatus ? warningStatus.status : "",
                    LogsViewUtility2.getTimeDiffFriendlyString(task.startTime, task.finishTime));
            } else {
                return format(Resources.NotStartedTaskLogStepOverviewAriaLabel, task.name, taskStatus.status);
            }

        } else {
            return "";
        }
    }

    private _getTaskOverviewAriaDescription(): string {
        let ariaDescription: string = Resources.TaskLogStepOptionsDescription;
        const task = this.props.task;
        const errors = LogsViewUtility2.getIssuesWithType(IssueType.Error, this.props.task);

        if (LogsViewUtility2.isLiveComponentVisible(task)) {
            ariaDescription += Resources.TaskLogStepOverviewLiveLogDescription;
        } else if (errors && errors.length > 0) {
            ariaDescription += Resources.TaskLogStepOverviewErrorLogsDescription;
        }
        return ariaDescription;
    }

    private _getLiveLogComponent(task: ITaskLog): React.ReactNode {
        let logLines: ILogLine[] = [];
        if (task.liveLogsData) {
            if (task.liveLogsData.logLines) {
                logLines = task.liveLogsData.logLines;
            }

            return (<LogsSummaryView logLines={this.state.initialTaskData.concat(logLines)} maxLogs={task.liveLogsData.maxLogs} fixedHeight={true} />);
        }

        return null;
    }

    private _getLogsErrorComponent(): React.ReactNode {
        const errors = LogsViewUtility2.getIssuesWithType(IssueType.Error, this.props.task);
        if (errors && errors.length > 0) {
            const logLines = LogsViewUtility2.getLogLinesFromIssues(errors, c_MaxErrorsLinesToShow);
            return (<LogsSummaryView onNavigateToLog={this._onNavigateToError} logLines={logLines} showSectionIcons={true} />);
        } else {
            return null;
        }
    }

    private _fetchLogsFromLogUrl = () => {
        const task = this.props.task;
        if (LogsViewUtility2.isLiveComponentVisible(task) && task.logUrl) {
            LogsViewUtility2.fetchLogs(task, true).then((content: string) => {
                // content could be huge, since this data is used to show it in inline logs view
                // so we should restrict to not to show more than 5000 characters at a time
                const newContent = content.slice(content.length - c_MaxCharactersToShowLiveLog);
                this.setState({
                    initialTaskData: LiveLogsParser.formatLogLine(newContent)
                });
            });
        }
    }

    private _onNavigateToError = (lineNumber?: number) => {
        this.props.showExpandedLogsView(this.props.task, lineNumber);
    }

    private _onMouseOver = () => {
        if (!this._isTaskLogStepSelected) {
            this._setShowDownloadLogButtonState(true);
            this._setShowTroubleShootErrorButtonState(true);
        }
    }

    private _onMouseLeave = () => {
        if (!this._isTaskLogStepSelected) {
            this._setShowDownloadLogButtonState(false);
            this._setShowTroubleShootErrorButtonState(false);
        }
    }

    private _onBlur = () => {
        this._isTaskLogStepSelected = false;
        this._setShowDownloadLogButtonState(false);
        this._setShowTroubleShootErrorButtonState(false);
    }

    private _onFocus = () => {
        this._isTaskLogStepSelected = true;
        this._setShowDownloadLogButtonState(true);
        this._setShowTroubleShootErrorButtonState(true);
    }

    private _setShowDownloadLogButtonState(shouldShowDownloadLogButton: boolean): void {
        this.setState({ shouldShowDownloadLogButton: shouldShowDownloadLogButton });
    }

    private _setShowTroubleShootErrorButtonState(shouldShowTroubleShootErrorButton: boolean): void {
        if (shouldShowTroubleShootErrorButton && !!this.props.taskHelpUrl) {
            const task = this.props.task;
            const errors = LogsViewUtility2.getIssuesWithType(IssueType.Error, task);
            const taskStatus = LogsViewUtility2.getTaskStatus(task.state, errors ? errors.length : 0, task.percentComplete);
            this.setState({ shouldShowTroubleShootErrorButton: taskStatus.statusProps === Statuses.Failed });
        } else {
            this.setState({ shouldShowTroubleShootErrorButton : false });
        }
    }

    private _getRightSection() {
        return (
            <div className="task-log-step-component-right-section flex">
                {this._getDownloadZipButton()}
                {this._getTroubleShootErrorButton()}
                {this._getDurationComponent()}
            </div>
        );
    }

    private _getDownloadZipButton() {
        return this.state && this.state.shouldShowDownloadLogButton ?
            (<div className={
                this.state.shouldShowTroubleShootErrorButton
                    ? "task-log-step-download-log-trouble-shoot-container"
                    : "task-log-step-download-log-container"
            }>
                <Button
                    subtle={true}
                    className="task-log-step-button"
                    iconProps={{ iconName: "ZipFolder" }}
                    onClick={this._onDownLoadLogClick}
                    disabled={!this.props.task.logUrl}
                    ariaLabel={Resources.DownloadLogMenuitemLabel}
                    tooltipProps={{ text: Resources.DownloadLogMenuitemLabel }}>
                </Button>
            </div>) : null;
    }

    private _onDownLoadLogClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (this.props.task && this.props.task.logUrl) {
            LogsViewUtility.downloadLog(this.props.task.logUrl);

            LogsTelemetryHelper.publishDownloadLogsTelemetry(this.props.pageContext, "TaskStepDownloadLog");
        }
    }

    private _onShowLogClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (this.props.showExpandedLogsView) {
            this.props.showExpandedLogsView(this.props.task);
        }
    }

    private _getDurationComponent(): JSX.Element {
        const task = this.props.task;
        const startTime: Date = task.startTime || new Date();
        return (
            <div className="task-log-clock-container" >
                {
                    (LogsViewUtility2.isTaskInProgress(task) || LogsViewUtility2.hasTaskCompleted(task)) ?
                        <Duration
                            startDate={startTime}
                            endDate={task.finishTime}
                        />
                        : null
                }
            </div>
        );
    }

    private _getTroubleShootErrorButton() {
        return this.state && this.state.shouldShowTroubleShootErrorButton ? (
            <div className="task-log-step-trouble-shoot-container">
                <Button
                    subtle={true}
                    className="task-log-step-button"
                    iconProps={{ iconName: "Repair" }}
                    disabled={!this.props.taskHelpUrl}
                    href={this.props.taskHelpUrl}
                    target={"_blank"}
                    ariaLabel={Resources.TroubleShootErrorsLabel}
                    tooltipProps={{ text: Resources.TroubleShootErrorsLabel }}
                ></Button>
            </div>
        ) : null;
    }

    private _isTaskLogStepSelected: boolean;
}
